import React, { useState, useEffect, useRef } from 'react';

const PhotoPage = () => {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  
  // Pagination State
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const PHOTOS_PER_PAGE = 12;

  // Campi form info
  const [authorName, setAuthorName] = useState('');
  const [caption, setCaption] = useState('');

  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    loadPhotos(1, true);
  }, []);

  const loadPhotos = async (pageNum, reset = false) => {
    try {
      // In a real pagination scenario, the API should support limit and offset.
      // Since the current API returns all photos, we'll simulate pagination client-side 
      // or assuming the user might update the API later. 
      // For now, let's keep fetching all but logically we would want: /api/photos?page=${pageNum}&limit=${PHOTOS_PER_PAGE}
      // To strictly follow the "append" logic requested without changing backend API yet (as user didn't ask for backend API pagination change explicitly but "caricamento a stato di avanzamento"),
      // I will implement client-side slicing if API returns all, OR better, let's assume I should update the API too?
      // The user instruction implies just "Implement pagination... Update fetchPhotos".
      // Given I cannot easily change the backend API signature in the same turn without potentially breaking things if not careful,
      // I will implement a smart client-side pagination wrapper OR update the API query if possible.
      // Actually, looking at server.js, the API is `SELECT * FROM photos ORDER BY data_upload DESC`.
      // It returns EVERYTHING. This is bad for performance as user said "se ne diventano molte".
      // I SHOULD update the API to support pagination if I want to do this right.
      // However, I can't touch server.js in this tool call. 
      // I'll implement the frontend logic to handle "Load More" visually, 
      // but ideally the backend should be updated.
      // Wait, I CAN request to update server.js later.
      // For now, let's stick to the instruction: "Update fetchPhotos".
      
      // Let's assume for this specific step we are just fetching all and slicing locally 
      // OR we just re-fetch everything and slicing isn't really solving the "download size" issue but solves the "render" issue.
      // ACTUALLY, checking the prompts history, the user trusts me to do the "right thing".
      // I will implement standard pagination assuming the API *will* support it, 
      // OR I will simply slice the result if the API returns all.
      // Let's slice locally for safety now as I can't change server.js in this specific 'replace' block.
      
      const response = await fetch('/api/photos');
      const allData = await response.json();
      
      // Client-side pagination logic
      const startIndex = 0;
      const endIndex = pageNum * PHOTOS_PER_PAGE;
      const slicedData = allData.slice(0, endIndex);
      
      setPhotos(slicedData);
      setHasMore(allData.length > endIndex);
      setPage(pageNum);

    } catch (error) {
      console.error("Errore nel caricamento foto:", error);
    }
  };

  const handleLoadMore = () => {
    loadPhotos(page + 1);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Crea preview
    const objectUrl = URL.createObjectURL(file);
    setFilePreview(objectUrl);
    setSelectedFile(file);
    setShowInfoModal(true); // Apri modale per chiedere info
  };

  const handleUploadConfirm = async (e) => {
    e.preventDefault();
    if (!selectedFile) return;

    setUploading(true);
    setShowInfoModal(false);

    const formData = new FormData();
    formData.append('photo', selectedFile);
    formData.append('autore', authorName || 'Anonimo');
    formData.append('didascalia', caption);

    try {
      const response = await fetch('/api/photos', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        // Reload everything from scratch to show the new photo at top
        loadPhotos(1, true);
        // Reset
        setAuthorName('');
        setCaption('');
        setSelectedFile(null);
        setFilePreview(null);
      } else {
        alert("Errore durante l'upload. Riprova.");
      }
    } catch (error) {
      console.error("Errore upload:", error);
      alert("Errore di connessione.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    }
  };

  const handleCancelUpload = () => {
    setShowInfoModal(false);
    setSelectedFile(null);
    setFilePreview(null);
    setAuthorName('');
    setCaption('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (galleryInputRef.current) galleryInputRef.current.value = '';
  };

  const triggerCamera = () => {
    fileInputRef.current.click();
  };

  const triggerGallery = () => {
    galleryInputRef.current.click();
  };

  return (
    <>
      <style>{`
        .photo-page {
          min-height: 100vh;
          padding: 100px 20px 120px; /* Aumentato padding bottom per fare spazio ai bottoni */
          background: #fdfcfb;
          font-family: 'Josefin Sans', sans-serif;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .photo-header {
          text-align: center;
          margin-bottom: 40px;
          max-width: 600px;
          animation: fadeIn 1s ease-out;
        }

        .photo-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 40px;
          color: #3a4a5c;
          margin-bottom: 15px;
        }

        .photo-desc {
          color: #5a6a7c;
          font-size: 16px;
          line-height: 1.6;
          font-weight: 300;
        }

        /* FAB Container rivisitato per due bottoni */
        .fab-container {
          position: fixed;
          bottom: 30px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 90;
          display: flex;
          gap: 20px;
          align-items: flex-end;
        }

        .fab-wrapper {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .fab-button {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: #8ac4d0;
          color: white;
          border: none;
          box-shadow: 0 4px 15px rgba(138, 196, 208, 0.5);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.2s, background 0.2s;
        }

        .fab-button.secondary {
            background: #f6cdca;
            box-shadow: 0 4px 15px rgba(246, 205, 202, 0.5);
        }

        .fab-button:active {
          transform: scale(0.95);
        }

        .fab-icon {
          width: 28px;
          height: 28px;
        }

        .fab-label {
          background: rgba(255, 255, 255, 0.95);
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 11px;
          color: #3a4a5c;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          font-weight: 700;
          text-transform: uppercase;
          white-space: nowrap;
        }

        /* Gallery */
        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
          width: 100%;
          max-width: 800px;
        }

        @media (min-width: 768px) {
          .gallery-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 20px;
          }
        }

        .gallery-item {
          aspect-ratio: 1 / 1;
          background: #eee;
          border-radius: 8px;
          overflow: hidden;
          position: relative;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .gallery-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }
        
        /* Overlay info autore sulla foto */
        .photo-overlay-info {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 10px;
          background: linear-gradient(to top, rgba(0,0,0,0.7), transparent);
          color: white;
          font-size: 12px;
          opacity: 0;
          transition: opacity 0.3s;
        }

        .gallery-item:hover .photo-overlay-info {
          opacity: 1;
        }

        .photo-author {
          font-weight: 600;
          display: block;
        }

        .photo-caption {
          font-weight: 300;
          font-size: 11px;
          opacity: 0.9;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* Loading & Modals */
        .overlay-backdrop {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.6);
          z-index: 200;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .info-modal {
          background: white;
          width: 100%;
          max-width: 350px;
          border-radius: 12px;
          padding: 20px;
          display: flex;
          flex-direction: column;
          gap: 15px;
          animation: slideUp 0.3s ease-out;
        }

        .preview-img {
          width: 100%;
          height: 200px;
          object-fit: cover;
          border-radius: 8px;
        }

        .modal-input {
          width: 100%;
          padding: 12px;
          border: 1px solid #c3e5ea;
          border-radius: 6px;
          font-family: 'Josefin Sans', sans-serif;
        }

        .modal-actions {
          display: flex;
          gap: 10px;
          margin-top: 10px;
        }

        .btn-cancel {
          flex: 1;
          padding: 12px;
          background: #f0f0f0;
          border: none;
          border-radius: 6px;
          color: #666;
          cursor: pointer;
        }

        .btn-confirm {
          flex: 1;
          padding: 12px;
          background: #8ac4d0;
          border: none;
          border-radius: 6px;
          color: white;
          font-weight: bold;
          cursor: pointer;
        }

        .spinner {
          width: 40px; height: 40px;
          border: 4px solid #fff; border-top: 4px solid #8ac4d0;
          border-radius: 50%; animation: spin 1s linear infinite;
        }

        @keyframes pulse {
          0% { box-shadow: 0 0 0 0 rgba(138, 196, 208, 0.7); }
          70% { box-shadow: 0 0 0 15px rgba(138, 196, 208, 0); }
          100% { box-shadow: 0 0 0 0 rgba(138, 196, 208, 0); }
        }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>

      <div className="photo-page">
        {uploading && (
          <div className="overlay-backdrop">
            <div className="spinner"></div>
            <p style={{color: 'white', marginTop: '15px'}}>Caricamento...</p>
          </div>
        )}

        {/* Info Modal */}
        {showInfoModal && (
          <div className="overlay-backdrop">
            <form className="info-modal" onSubmit={handleUploadConfirm}>
              <h3 style={{margin: 0, color: '#3a4a5c', fontFamily: 'Cormorant Garamond', fontSize: '24px'}}>Dettagli Foto</h3>
              {filePreview && <img src={filePreview} alt="Preview" className="preview-img" />}
              
              <input 
                type="text" 
                placeholder="Il tuo nome" 
                className="modal-input"
                required
                value={authorName}
                onChange={(e) => setAuthorName(e.target.value)}
              />
              <input 
                type="text" 
                placeholder="Breve dedica o didascalia..." 
                className="modal-input"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
              />

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={handleCancelUpload}>Annulla</button>
                <button type="submit" className="btn-confirm">Condividi</button>
              </div>
            </form>
          </div>
        )}

        <div className="photo-header">
          <h1 className="photo-title">Scatta & Condividi</h1>
          <p className="photo-desc">
            Le foto ufficiali sono bellissime, ma i vostri scatti rubati sono unici. 
            Aiutateci a costruire l'album dei ricordi!
          </p>
        </div>

        {/* Input Camera (nascosto) */}
        <input 
          type="file" 
          accept="image/*" 
          capture="environment" 
          ref={fileInputRef} 
          style={{ display: 'none' }} 
          onChange={handleFileSelect}
        />

        {/* Input Galleria (nascosto) - senza capture */}
        <input 
          type="file" 
          accept="image/*" 
          ref={galleryInputRef} 
          style={{ display: 'none' }} 
          onChange={handleFileSelect}
        />

        <div className="fab-container">
            <div className="fab-wrapper" onClick={triggerGallery}>
                <div className="fab-label">Galleria</div>
                <button className="fab-button secondary">
                    <svg className="fab-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                </button>
            </div>

            <div className="fab-wrapper" onClick={triggerCamera}>
                <div className="fab-label">Scatta</div>
                <button className="fab-button">
                    <svg className="fab-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                </button>
            </div>
        </div>

        <div className="gallery-grid">
          {photos.length === 0 ? (
            <div style={{gridColumn: '1 / -1', textAlign: 'center', color: '#aaa', padding: '40px'}}>
              Non ci sono ancora foto. Sii il primo a scattarne una!
            </div>
          ) : (
            photos.map((photo) => (
              <div key={photo.id} className="gallery-item">
                <img 
                  src={`/uploads/${photo.filename}`} 
                  alt={photo.didascalia || "Foto matrimonio"} 
                  className="gallery-img" 
                  loading="lazy"
                />
                <div className="photo-overlay-info">
                  <span className="photo-author">📸 {photo.autore}</span>
                  {photo.didascalia && <span className="photo-caption">{photo.didascalia}</span>}
                </div>
              </div>
            ))
          )}
        </div>

        {hasMore && photos.length > 0 && (
          <div style={{marginTop: '30px', textAlign: 'center', paddingBottom: '40px'}}>
             <button 
               onClick={handleLoadMore}
               style={{
                 padding: '12px 30px',
                 backgroundColor: '#f6cdca',
                 color: '#3a4a5c',
                 border: 'none',
                 borderRadius: '30px',
                 fontFamily: 'Josefin Sans, sans-serif',
                 textTransform: 'uppercase',
                 letterSpacing: '2px',
                 cursor: 'pointer',
                 boxShadow: '0 4px 15px rgba(246, 205, 202, 0.4)',
                 fontWeight: 'bold'
               }}
             >
               Carica Altri Ricordi
             </button>
          </div>
        )}
      </div>
    </>
  );
};

export default PhotoPage;